import {
  KnownDeliveryContainerDtoType,
  KnownTimelineEventDeliveryContainerDtoStatus,
  KnownTimelineEventDeliveryContainerDtoAvailability,
  TimelineEventDto
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { TimelineEventProps } from '../../../../ui/components/pure/timeline/timeline.types';
import { makeTimelineEvent } from '../../../orders/details/logic/timeline/order-timeline.map';
import { Colors } from '../../../../ui/theme/colors';

export interface LockerTimelineEvent {
  props: TimelineEventProps;
  statusColor: string;
}

export interface LockerTimelineDeliveryGroup {
  type: 'delivery';
  deliveryId: string;
  deliveryLabel: string;
  deliveryType?: string;
  deliveryDigitalSignature?: string;
  restricted?: boolean;
  events: LockerTimelineEvent[];
  earliestTimestamp: Date;
}

export interface LockerTimelineSessionGroup {
  type: 'session';
  sessionId: string;
  sessionChannel?: string;
  sessionUserDisplayName?: string;
  deliveryGroups: LockerTimelineDeliveryGroup[];
  ungroupedEvents: LockerTimelineEvent[];
  earliestTimestamp: Date;
}

export interface LockerTimelineUngroupedGroup {
  type: 'ungrouped';
  events: LockerTimelineEvent[];
  earliestTimestamp: Date;
}

export type LockerTimelineGroup =
  | LockerTimelineSessionGroup
  | LockerTimelineDeliveryGroup
  | LockerTimelineUngroupedGroup;

interface MappedEvent {
  dto: TimelineEventDto;
  props: TimelineEventProps;
  statusColor: string;
}

const availabilityLabels: Record<string, string> = {
  [KnownTimelineEventDeliveryContainerDtoAvailability.Available]: 'available',
  [KnownTimelineEventDeliveryContainerDtoAvailability.Reserved]: 'reserved',
  [KnownTimelineEventDeliveryContainerDtoAvailability.InUse]: 'in use'
};

function getStatusColor(dto: TimelineEventDto): string {
  return dto.deliveryContainer?.status === KnownTimelineEventDeliveryContainerDtoStatus.Active
    ? Colors.collectedGreen
    : Colors.cancelledDeletedRed;
}

function toLockerTimelineEvent(e: MappedEvent): LockerTimelineEvent {
  return { props: e.props, statusColor: e.statusColor };
}

function getEarliestTimestamp(events: MappedEvent[]): Date {
  return events.reduce(
    (earliest, e) => (e.dto.timestamp < earliest ? e.dto.timestamp : earliest),
    events[0].dto.timestamp
  );
}

function buildDeliveryGroups(events: MappedEvent[]): LockerTimelineDeliveryGroup[] {
  const deliveryMap = new Map<string, MappedEvent[]>();
  const order: string[] = [];

  for (const event of events) {
    const deliveryId = event.dto.delivery?.id ?? '_none_';
    if (!deliveryMap.has(deliveryId)) {
      deliveryMap.set(deliveryId, []);
      order.push(deliveryId);
    }
    deliveryMap.get(deliveryId)!.push(event);
  }

  return order
    .filter((id) => id !== '_none_')
    .map((deliveryId) => {
      const groupEvents = deliveryMap.get(deliveryId)!;
      const firstDelivery = groupEvents[0]?.dto.delivery;
      return {
        type: 'delivery' as const,
        deliveryId,
        deliveryLabel: deliveryId,
        deliveryType: firstDelivery?.deliveryType,
        deliveryDigitalSignature: firstDelivery?.digitalSignature,
        restricted: groupEvents[0]?.dto.restricted,
        events: groupEvents.map(toLockerTimelineEvent),
        earliestTimestamp: getEarliestTimestamp(groupEvents)
      };
    })
    .sort((a, b) => b.earliestTimestamp.getTime() - a.earliestTimestamp.getTime());
}

export function getDayBounds(date: Date): { from: Date; to: Date } {
  const from = new Date(date);
  from.setHours(0, 0, 0, 0);
  const to = new Date(date);
  to.setHours(23, 59, 59, 999);
  return { from, to };
}

export function getFirstStatusColor(groups: LockerTimelineGroup[]): string | undefined {
  for (const group of groups) {
    if (group.type === 'session') {
      for (const dg of group.deliveryGroups) {
        if (dg.events.length > 0) return dg.events[0].statusColor;
      }
      if (group.ungroupedEvents.length > 0) return group.ungroupedEvents[0].statusColor;
    } else {
      if (group.events.length > 0) return group.events[0].statusColor;
    }
  }
  return undefined;
}

export function getLastStatusColor(groups: LockerTimelineGroup[]): string | undefined {
  for (let i = groups.length - 1; i >= 0; i--) {
    const group = groups[i];
    if (group.type === 'session') {
      if (group.ungroupedEvents.length > 0) return group.ungroupedEvents[group.ungroupedEvents.length - 1].statusColor;
      for (let j = group.deliveryGroups.length - 1; j >= 0; j--) {
        const dg = group.deliveryGroups[j];
        if (dg.events.length > 0) return dg.events[dg.events.length - 1].statusColor;
      }
    } else {
      if (group.events.length > 0) return group.events[group.events.length - 1].statusColor;
    }
  }
  return undefined;
}

function mergeAdjacentUngroupedGroups(groups: LockerTimelineGroup[]): LockerTimelineGroup[] {
  const result: LockerTimelineGroup[] = [];

  for (const group of groups) {
    const prev = result[result.length - 1];
    if (group.type === 'ungrouped' && prev?.type === 'ungrouped') {
      prev.events.push(...group.events);
      if (group.earliestTimestamp < prev.earliestTimestamp) {
        prev.earliestTimestamp = group.earliestTimestamp;
      }
    } else {
      result.push(group);
    }
  }

  return result;
}

export function groupTimelineEvents(
  events: TimelineEventDto[],
  containerType?: KnownDeliveryContainerDtoType | string
): LockerTimelineGroup[] {
  const mapped: MappedEvent[] = events
    .map((dto) => {
      const props = makeTimelineEvent(dto);
      return props ? { dto, props, statusColor: getStatusColor(dto) } : undefined;
    })
    .filter((e): e is MappedEvent => !!e);

  const isLocker = containerType === KnownDeliveryContainerDtoType.Locker;
  const hasAnyAvailability = isLocker && mapped.some((e) => e.dto.deliveryContainer?.availability !== undefined);
  if (hasAnyAvailability) {
    for (const e of mapped) {
      const raw = e.dto.deliveryContainer?.availability;
      e.props = { ...e.props, availability: raw === undefined ? '' : availabilityLabels[raw] ?? raw };
    }
  }

  const withSession: MappedEvent[] = [];
  const withoutSession: MappedEvent[] = [];

  for (const event of mapped) {
    if (event.dto.session?.id) {
      withSession.push(event);
    } else {
      withoutSession.push(event);
    }
  }

  const groups: LockerTimelineGroup[] = [];

  // Group events with sessions
  const sessionMap = new Map<string, MappedEvent[]>();
  const sessionOrder: string[] = [];
  for (const event of withSession) {
    const sessionId = event.dto.session!.id;
    if (!sessionMap.has(sessionId)) {
      sessionMap.set(sessionId, []);
      sessionOrder.push(sessionId);
    }
    sessionMap.get(sessionId)!.push(event);
  }

  for (const sessionId of sessionOrder) {
    const sessionEvents = sessionMap.get(sessionId)!;
    const withDelivery = sessionEvents.filter((e) => e.dto.delivery?.id);
    const withoutDelivery = sessionEvents.filter((e) => !e.dto.delivery?.id);

    groups.push({
      type: 'session',
      sessionId,
      sessionChannel: sessionEvents[0]?.dto.session?.channel,
      sessionUserDisplayName: sessionEvents[0]?.dto.session?.user?.displayName,
      deliveryGroups: buildDeliveryGroups(withDelivery),
      ungroupedEvents: withoutDelivery.map(toLockerTimelineEvent),
      earliestTimestamp: getEarliestTimestamp(sessionEvents)
    });
  }

  // Group events without sessions by delivery
  const standaloneDeliveryMap = new Map<string, MappedEvent[]>();
  const standaloneOrder: string[] = [];
  const orphanEvents: MappedEvent[] = [];

  for (const event of withoutSession) {
    const deliveryId = event.dto.delivery?.id;
    if (deliveryId) {
      if (!standaloneDeliveryMap.has(deliveryId)) {
        standaloneDeliveryMap.set(deliveryId, []);
        standaloneOrder.push(deliveryId);
      }
      standaloneDeliveryMap.get(deliveryId)!.push(event);
    } else {
      orphanEvents.push(event);
    }
  }

  for (const deliveryId of standaloneOrder) {
    const deliveryEvents = standaloneDeliveryMap.get(deliveryId)!;
    const firstDelivery = deliveryEvents[0]?.dto.delivery;
    groups.push({
      type: 'delivery',
      deliveryId,
      deliveryLabel: deliveryId,
      deliveryType: firstDelivery?.deliveryType,
      deliveryDigitalSignature: firstDelivery?.digitalSignature,
      restricted: deliveryEvents[0]?.dto.restricted,
      events: deliveryEvents.map(toLockerTimelineEvent),
      earliestTimestamp: getEarliestTimestamp(deliveryEvents)
    });
  }

  // Each orphan event becomes its own ungrouped group for correct timestamp positioning
  for (const event of orphanEvents) {
    groups.push({
      type: 'ungrouped',
      events: [toLockerTimelineEvent(event)],
      earliestTimestamp: event.dto.timestamp
    });
  }

  // Sort groups by earliest timestamp (descending — newest sessions/deliveries first)
  groups.sort((a, b) => b.earliestTimestamp.getTime() - a.earliestTimestamp.getTime());

  // Merge consecutive ungrouped groups so they render as one section
  return mergeAdjacentUngroupedGroups(groups);
}
